import React, { useState,useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Custom SVG Icons as components
const ArrowUpIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#2ecc71">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

const CrossIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#e74c3c">
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
  </svg>
);

const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
  </svg>
);

const ComparisonPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const products = location.state?.products || [];
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [expandedSections, setExpandedSections] = useState({
    technology: true,
    hardware: true,
    firmware: true
  });
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024;
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const styles = {
    container: {
      padding: isMobile ? '10px' : '20px',
      maxWidth: '100%',
      margin: '0 auto',
      fontFamily: 'Inter, system-ui, sans-serif',
      overflowX: 'hidden'
    },
    header: {
      marginBottom: isMobile ? '20px' : '40px',
      position: 'relative',
      textAlign: 'center',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '16px' : '24px',
      width: '100%'
    },
    heading: {
      fontSize: isMobile ? '24px' : isTablet ? '32px' : '52px',
      fontWeight: 'bold',
      color: '#400684',
      margin: '0',
      width: '100%',
      fontFamily: 'Oswald, sans-serif',
      textAlign: isMobile ? 'center' : 'center',
      wordWrap: 'break-word',
      lineHeight: 1.2,
      '@media (max-width: 480px)': {
        fontSize: '20px',
      }
    },
    backButton: {
      position: 'relative',
      padding: isMobile ? '8px 16px' : '10px 30px',
      background: '#400684',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '300px' : 'none',
      justifyContent: 'center',
      fontSize: isMobile ? '14px' : '16px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      '&:hover': {
        background: '#2f0563',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
      '@media (max-width: 480px)': {
        padding: '6px 12px',
        fontSize: '13px',
      }
    },
    comparisonGrid: {
      display: 'grid',
      gridTemplateColumns: `220px repeat(${products.length}, 1fr)`,
      gap: '1px',
      background: '#e0e0e0',
      border: '1px solid #e0e0e0',
      borderRadius: '12px',
      overflow: 'auto',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      '@media (max-width: 768px)': {
        gridTemplateColumns: '150px repeat(auto-fit, minmax(120px, 1fr))',
        fontSize: '14px',
        borderRadius: '8px'
      }
    },
    scrollContainer: {
      overflowX: 'auto',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
      msOverflowStyle: '-ms-autohiding-scrollbar'
    },
    cell: {
      padding: '16px',
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      minWidth: '120px',
      '@media (max-width: 768px)': {
        padding: '12px',
        minWidth: '100px'
      }
    },
    headerCell: {
      background: '#f8f9fa',
      fontWeight: '600',
      padding: '24px 16px',
      color: '#2c3e50',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      '@media (max-width: 768px)': {
        padding: '16px 12px'
      }
    },
    productImage: {
      width: '100%',
      maxWidth: '180px',
      height: 'auto',
      marginBottom: '12px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      '@media (max-width: 768px)': {
        maxWidth: '120px'
      }
    },
    categoryHeader: {
      background: 'linear-gradient(45deg, #400684, #2196f3)',
      color: '#fff',
      fontWeight: '600',
      gridColumn: '1 / -1',
      padding: '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background 0.3s ease',
      '@media (max-width: 768px)': {
        padding: '12px 16px'
      }
    },
    featureCell: {
      padding: '16px',
      background: '#fff',
      color: '#34495e',
      display: 'flex',
      alignItems: 'center',
      fontWeight: '500',
      '@media (max-width: 768px)': {
        padding: '12px',
        fontSize: '13px'
      }
    },
    valueCell: {
      padding: '16px',
      background: '#fff',
      color: '#34495e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '@media (max-width: 768px)': {
        padding: '12px',
        fontSize: '13px'
      }
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? <CheckIcon /> : <CrossIcon />;
    }
    if (value && typeof value === 'object' && 'value' in value && 'label' in value) {
      return value.label;
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (value === null || value === undefined) {
      return '-';
    }
    return String(value);
  };

  const renderTechnologySection = () => {
    if (!products[0]?.specs?.technology) return null;

    return (
      <React.Fragment>
        <div 
          style={styles.categoryHeader}
          onClick={() => toggleSection('technology')}
        >
          Technology
          {expandedSections.technology ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </div>
        {expandedSections.technology && (
          <>
            {products[0].specs.technology.cellular && 
              Object.entries(products[0].specs.technology.cellular).map(([key]) => (
                <React.Fragment key={`cellular-${key}`}>
                  <div style={styles.featureCell}>
                  {key.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  {products.map((product, index) => (
                    <div key={`cellular-${key}-${index}`} style={styles.valueCell}>
                      {renderValue(product.specs.technology.cellular[key])}
                    </div>
                  ))}
                </React.Fragment>
              ))
            }
            
            {products[0].specs.technology.connectivity &&
              Object.entries(products[0].specs.technology.connectivity).map(([key]) => (
                <React.Fragment key={`connectivity-${key}`}>
                  <div style={styles.featureCell}>
                  {key.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  {products.map((product, index) => (
                    <div key={`connectivity-${key}-${index}`} style={styles.valueCell}>
                      {renderValue(product.specs.technology.connectivity[key])}
                    </div>
                  ))}
                </React.Fragment>
              ))
            }

            {products[0].specs.technology.features &&
              Object.entries(products[0].specs.technology.features).map(([key]) => (
                <React.Fragment key={`features-${key}`}>
                  <div style={styles.featureCell}>
                  {key.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  {products.map((product, index) => (
                    <div key={`features-${key}-${index}`} style={styles.valueCell}>
                      {renderValue(product.specs.technology.features[key])}
                    </div>
                  ))}
                </React.Fragment>
              ))
            }
          </>
        )}
      </React.Fragment>
    );
  };

  const renderHardwareSection = () => {
    if (!products[0]?.specs?.hardware) return null;

    return (
      <React.Fragment>
        <div 
          style={styles.categoryHeader}
          onClick={() => toggleSection('hardware')}
        >
          Hardware
          {expandedSections.hardware ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </div>
        {expandedSections.hardware && (
          Object.entries(products[0].specs.hardware).map(([key]) => (
            <React.Fragment key={`hardware-${key}`}>
              <div style={styles.featureCell}>
              {key.replace(/_/g, ' ').toUpperCase()}
              </div>
              {products.map((product, index) => (
                <div key={`hardware-${key}-${index}`} style={styles.valueCell}>
                  {renderValue(product.specs.hardware[key])}
                </div>
              ))}
            </React.Fragment>
          ))
        )}
      </React.Fragment>
    );
  };

  const renderFirmwareSection = () => {
    if (!products[0]?.specs?.firmware) return null;

    return (
      <React.Fragment>
        <div 
          style={styles.categoryHeader}
          onClick={() => toggleSection('firmware')}
        >
          Firmware
          {expandedSections.firmware ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </div>
        {expandedSections.firmware && (
          Object.entries(products[0].specs.firmware).map(([key]) => (
            <React.Fragment key={`firmware-${key}`}>
              <div style={styles.featureCell}>
              {key.replace(/_/g, ' ').toUpperCase()}
              </div>
              {products.map((product, index) => (
                <div key={`firmware-${key}-${index}`} style={styles.valueCell}>
                  {renderValue(product.specs.firmware[key])}
                </div>
              ))}
            </React.Fragment>
          ))
        )}
      </React.Fragment>
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (products.length === 0) {
    return (
      <div style={styles.container}>
        <h1 style={styles.heading}>No products selected for comparison</h1>
        <button style={styles.backButton} onClick={handleBack}>
          <BackIcon /> Back to Products
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={handleBack}>
          <BackIcon /> Back to Products
        </button>
        <h1 style={styles.heading}>PRODUCT COMPARISON</h1>
      </div>
      <div style={styles.scrollContainer}>
        <div style={styles.comparisonGrid}>
          <div style={{...styles.cell, ...styles.headerCell}}>Features</div>
          {products.map((product, index) => (
            <div key={`product-${index}`} style={{...styles.cell, ...styles.headerCell}}>
              <img src={product.images[0]} alt={product.name} style={styles.productImage} />
              {product.name}
            </div>
          ))}
          
          {renderTechnologySection()}
          {renderHardwareSection()}
          {renderFirmwareSection()}
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;